import React, { useState } from 'react';
import { Instagram, MessageCircle, Mail, Clock, FileText, Send, MapPin, CheckCircle, User, Phone, Shirt } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import Layout from '../components/layout/Layout';
import { BRAND, COLLECTIONS, PRODUCT_OPTIONS } from '../data/mock';

const WHATSAPP = '918889616972';
const INSTAGRAM_USER = 'aura_printiqo';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    collection: '',
    product: '',
    size: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const buildMessage = () =>
    `Hi AuraPrintiqo! 👋\n\n*New Order Inquiry*\n━━━━━━━━━━━━━━━━\n*Name:* ${formData.name}\n*Phone:* ${formData.phone}\n*Email:* ${formData.email || 'Not provided'}\n\n*Order Details:*\n• Collection: ${formData.collection || 'Not selected'}\n• Product: ${formData.product || 'Not selected'}\n• Size: ${formData.size || 'Not selected'}\n\n*Message:*\n${formData.message || 'No additional message'}\n\nI'd like to place an order. Please share the next steps!`;

  const handleWhatsAppSubmit = () => {
    const url = `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(buildMessage())}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    setSubmitted(true);
  };

  const handleInstagramSubmit = () => {
    const msg = buildMessage()
      .replace(/\n/g, '%0A')
      .replace(/ /g, '%20')
      .replace(/\*/g, '')
      .replace(/━/g, '-');
    window.open(`https://ig.me/m/${INSTAGRAM_USER}?text=${msg}`, '_blank', 'noopener,noreferrer');
    setSubmitted(true);
  };

  const contactMethods = [
    {
      icon: Instagram,
      title: 'Instagram DM',
      description: 'Our fastest response channel! DM us your photos and queries.',
      action: 'Message on Instagram',
      onClick: () => window.open(BRAND.instagram, '_blank', 'noopener,noreferrer'),
      color: 'from-purple-500 via-pink-500 to-orange-500'
    },
    {
      icon: MessageCircle,
      title: 'WhatsApp',
      description: 'Chat with us directly for quick responses and order updates.',
      action: 'Chat on WhatsApp',
      onClick: () =>
        window.open(
          `https://wa.me/${WHATSAPP}?text=${encodeURIComponent('Hi! I want to place an order at AuraPrintiqo.')}`,
          '_blank',
          'noopener,noreferrer'
        ),
      color: 'from-green-500 to-green-600'
    },
    {
      icon: Mail,
      title: 'Email Us',
      description: 'For detailed queries, bulk orders, or partnership inquiries.',
      action: 'Send Email',
      onClick: () => (window.location.href = `mailto:${BRAND.email}`),
      color: 'from-[hsl(350,70%,65%)] to-[hsl(20,90%,70%)]'
    }
  ];

  return (
    <Layout>
      {/* Hero */}
      <section className="py-16 sm:py-24 bg-gradient-to-br from-[hsl(var(--cream))] via-white to-[hsl(var(--peach))]/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-block px-4 py-2 bg-[hsl(350,70%,65%)]/10 rounded-full text-sm font-medium text-[hsl(350,70%,55%)] mb-4">
              Get In Touch
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
              Let's Create Something
              <span className="gradient-text"> Amazing Together</span>
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed">
              Have questions? Want to place an order? We're here to help!
              Reach out through any of our channels below.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {contactMethods.map((method, index) => (
              <Card key={index} className="border-0 shadow-soft hover:shadow-soft-lg transition-all duration-300 hover-lift overflow-hidden">
                <CardContent className="p-0">
                  <div className={`h-2 bg-gradient-to-r ${method.color}`} />
                  <div className="p-8">
                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-r ${method.color} flex items-center justify-center mb-6`}>
                      <method.icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{method.title}</h3>
                    <p className="text-gray-600 mb-6 leading-relaxed">{method.description}</p>
                    <Button
                      onClick={method.onClick}
                      className={`w-full bg-gradient-to-r ${method.color} hover:opacity-90 text-white`}
                    >
                      {method.action}
                      <Send className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Order Form */}
      <section id="order-form" className="py-16 bg-gradient-to-b from-[hsl(var(--cream))] to-white scroll-mt-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-[hsl(var(--mint))]/50 rounded-full text-sm font-medium text-[hsl(var(--mint-dark))] mb-4">
              <FileText className="w-4 h-4 inline mr-1" />
              Order Form
            </span>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Place Your Order</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Fill out the form and choose WhatsApp or Instagram to send your inquiry directly to us.
            </p>
          </div>

          <Card className="border-0 shadow-soft-lg overflow-hidden">
            <CardContent className="p-8 sm:p-10">
              {!submitted ? (
                <form className="space-y-6">
                  {/* Personal Details */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <User className="w-5 h-5 text-[hsl(350,70%,60%)]" />
                      Personal Details
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="name" className="text-gray-700 font-medium">Full Name *</Label>
                        <Input
                          id="name"
                          placeholder="Enter your name"
                          value={formData.name}
                          onChange={(e) => handleInputChange('name', e.target.value)}
                          className="mt-2"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone" className="text-gray-700 font-medium">WhatsApp Number *</Label>
                        <Input
                          id="phone"
                          placeholder="+91 XXXXX XXXXX"
                          value={formData.phone}
                          onChange={(e) => handleInputChange('phone', e.target.value)}
                          className="mt-2"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-gray-700 font-medium">Email (Optional)</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="your@email.com"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        className="mt-2"
                      />
                    </div>
                  </div>

                  {/* Order Details */}
                  <div className="space-y-4 pt-4 border-t border-gray-100">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <Shirt className="w-5 h-5 text-[hsl(350,70%,60%)]" />
                      Order Details
                    </h3>
                    <div className="grid sm:grid-cols-3 gap-4">
                      <div>
                        <Label className="text-gray-700 font-medium">Collection *</Label>
                        <Select onValueChange={(value) => handleInputChange('collection', value)}>
                          <SelectTrigger className="mt-2">
                            <SelectValue placeholder="Select collection" />
                          </SelectTrigger>
                          <SelectContent>
                            {COLLECTIONS.map((collection) => (
                              <SelectItem key={collection.id} value={collection.title}>
                                {collection.title}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-gray-700 font-medium">Product *</Label>
                        <Select onValueChange={(value) => handleInputChange('product', value)}>
                          <SelectTrigger className="mt-2">
                            <SelectValue placeholder="Select product" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="T-Shirt">T-Shirt</SelectItem>
                            <SelectItem value="Oversized Tee">Oversized Tee</SelectItem>
                            <SelectItem value="Hoodie">Hoodie</SelectItem>
                            <SelectItem value="Sweatshirt">Sweatshirt</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-gray-700 font-medium">Size *</Label>
                        <Select onValueChange={(value) => handleInputChange('size', value)}>
                          <SelectTrigger className="mt-2">
                            <SelectValue placeholder="Select size" />
                          </SelectTrigger>
                          <SelectContent>
                            {PRODUCT_OPTIONS.tshirt.sizes.map((size) => (
                              <SelectItem key={size} value={size}>{size}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Message */}
                  <div className="space-y-4 pt-4 border-t border-gray-100">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <MessageCircle className="w-5 h-5 text-[hsl(350,70%,60%)]" />
                      Your Message
                    </h3>
                    <div>
                      <Label htmlFor="message" className="text-gray-700 font-medium">
                        Tell us about your order (Optional)
                      </Label>
                      <Textarea
                        id="message"
                        placeholder="Describe your design idea, share reference links, or ask any questions..."
                        value={formData.message}
                        onChange={(e) => handleInputChange('message', e.target.value)}
                        className="mt-2"
                        rows={4}
                      />
                    </div>
                  </div>

                  {/* Submit Buttons */}
                  <div className="pt-6 space-y-4">
                    <p className="text-sm text-gray-500 text-center font-medium">
                      Choose how you'd like to send your inquiry:
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Button
                        type="button"
                        onClick={handleWhatsAppSubmit}
                        className="flex-1 bg-green-500 hover:bg-green-600 text-white py-6 text-base"
                        disabled={!formData.name || !formData.phone}
                      >
                        <MessageCircle className="w-5 h-5 mr-2" />
                        Send via WhatsApp
                      </Button>
                      <Button
                        type="button"
                        onClick={handleInstagramSubmit}
                        className="flex-1 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 hover:opacity-90 text-white py-6 text-base"
                        disabled={!formData.name || !formData.phone}
                      >
                        <Instagram className="w-5 h-5 mr-2" />
                        Send via Instagram DM
                      </Button>
                    </div>
                    <p className="text-xs text-gray-400 text-center">
                      * Name and WhatsApp number are required. Clicking a button opens the app with your details pre-filled.
                    </p>
                  </div>
                </form>
              ) : (
                <div className="text-center py-12">
                  <div className="w-20 h-20 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-6">
                    <CheckCircle className="w-10 h-10 text-green-500" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Message Sent!</h3>
                  <p className="text-gray-600 mb-2">
                    Your inquiry has been sent via WhatsApp / Instagram DM.
                  </p>
                  <p className="text-gray-500 text-sm mb-6">
                    We'll get back to you within 2–4 hours during working hours.
                  </p>
                  <Button onClick={() => setSubmitted(false)} variant="outline">
                    Submit Another Order
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-0 shadow-soft">
              <CardContent className="p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-[hsl(var(--lavender))] flex items-center justify-center">
                    <Clock className="w-6 h-6 text-[hsl(var(--lavender-dark))]" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Working Hours</h3>
                </div>
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-gray-600">Monday - Friday</span>
                    <span className="font-semibold text-gray-900">10:00 AM - 8:00 PM</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-gray-600">Saturday</span>
                    <span className="font-semibold text-gray-900">10:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between items-center py-3">
                    <span className="text-gray-600">Sunday</span>
                    <span className="font-semibold text-gray-900">Closed</span>
                  </div>
                </div>
                <p className="text-sm text-gray-500 mt-6">
                  *Response time may vary during weekends and holidays
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-soft">
              <CardContent className="p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-[hsl(var(--mint))] flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-[hsl(var(--mint-dark))]" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Quick Info</h3>
                </div>
                <div className="space-y-4">
                  <div className="py-3 border-b border-gray-100">
                    <span className="text-sm text-gray-500 block mb-1">Email</span>
                    <button
                      onClick={() => (window.location.href = `mailto:${BRAND.email}`)}
                      className="font-semibold text-gray-900 hover:text-[hsl(350,70%,55%)] transition-colors"
                    >
                      {BRAND.email}
                    </button>
                  </div>
                  <div className="py-3 border-b border-gray-100">
                    <span className="text-sm text-gray-500 block mb-1">Instagram</span>
                    <button
                      onClick={() => window.open(BRAND.instagram, '_blank', 'noopener,noreferrer')}
                      className="font-semibold text-gray-900 hover:text-[hsl(350,70%,55%)] transition-colors"
                    >
                      @aura_printiqo
                    </button>
                  </div>
                  <div className="py-3">
                    <span className="text-sm text-gray-500 block mb-1">Delivery</span>
                    <span className="font-semibold text-gray-900">Pan-India Shipping</span>
                  </div>
                </div>
                <p className="text-sm text-gray-500 mt-6">
                  Average response time: Within 2-4 hours
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-gradient-to-br from-[hsl(350,70%,65%)] to-[hsl(20,90%,65%)]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to Start Your Order?
          </h2>
          <p className="text-white/90 text-lg mb-10 max-w-2xl mx-auto">
            DM us on Instagram for the fastest response! Just send your photo and we'll take it from there.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              onClick={() => window.open(BRAND.instagram, '_blank', 'noopener,noreferrer')}
              className="bg-white text-gray-900 hover:bg-gray-100 px-10 shadow-lg text-lg"
            >
              <Instagram className="w-6 h-6 mr-2" />
              DM Us on Instagram
            </Button>
            <Button
              size="lg"
              onClick={() => window.open(`https://wa.me/${WHATSAPP}?text=${encodeURIComponent('Hi! I want to place an order at AuraPrintiqo.')}`, '_blank', 'noopener,noreferrer')}
              className="bg-green-500 hover:bg-green-600 text-white px-10 shadow-lg text-lg"
            >
              <MessageCircle className="w-6 h-6 mr-2" />
              WhatsApp Us
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ContactPage;
